import React, { useState, useEffect } from 'react';
import { Activity, CheckCircle, XCircle, Loader } from 'lucide-react';

function StravaConnect() {
  const [stravaStatus, setStravaStatus] = useState({
    connected: false,
    loading: true
  });

  useEffect(() => {
    checkStravaStatus();
    
    // Vérifier si on revient de l'autorisation Strava
    const urlParams = new URLSearchParams(window.location.search);
    const stravaParam = urlParams.get('strava');
    
    if (stravaParam === 'connected') {
      alert('✅ Strava connecté avec succès !');
      checkStravaStatus();
      // Nettoyer l'URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (stravaParam === 'error') {
      alert('❌ Erreur lors de la connexion à Strava');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const checkStravaStatus = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/strava/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStravaStatus({
          connected: data.connected,
          stravaId: data.stravaId,
          loading: false
        });
      }
    } catch (error) {
      console.error('Erreur vérification Strava:', error);
      setStravaStatus({ connected: false, loading: false });
    }
  };

  const handleConnect = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/strava/connect`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Rediriger vers Strava pour autorisation
        window.location.href = data.authUrl;
      }
    } catch (error) {
      console.error('Erreur connexion Strava:', error);
      alert('Erreur lors de la connexion à Strava');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir déconnecter Strava ?')) {
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/strava/disconnect`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('✅ Strava déconnecté');
        setStravaStatus({ connected: false, loading: false });
      }
    } catch (error) {
      console.error('Erreur déconnexion Strava:', error);
      alert('Erreur lors de la déconnexion');
    }
  };

  if (stravaStatus.loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-center">
          <Loader className="animate-spin text-indigo-600" size={24} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-xl shadow-lg p-6 border border-slate-700">
      <h2 className="text-xl font-bold text-white mb-4">
        Connexions
      </h2>

      {/* Section Strava */}
      <div className="border-2 border-slate-700 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-orange-100 p-3 rounded-lg">
              <Activity size={24} className="text-orange-600" />
            </div>
            <div>
              <h3 className="font-bold text-white">Strava</h3>
              <p className="text-sm text-white">
                {stravaStatus.connected 
                  ? 'Synchronisation automatique activée'
                  : 'Connectez Strava pour synchroniser vos courses'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {stravaStatus.connected ? (
              <>
                <CheckCircle size={20} className="text-green-600" />
                <button
                  onClick={handleDisconnect}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold"
                >
                  Déconnecter
                </button>
              </>
            ) : (
              <>
                <XCircle size={20} className="text-gray-400" />
                <button
                  onClick={handleConnect}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition font-semibold flex items-center space-x-2"
                >
                  <Activity size={18} />
                  <span>Connecter Strava</span>
                </button>
              </>
            )}
          </div>
        </div>

        {stravaStatus.connected && (
        <div className="mt-4 space-y-3">
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center space-x-2 text-sm text-green-800">
                <CheckCircle size={16} className="text-green-600" />
                <span className="font-semibold">Avantages :</span>
            </div>
            <ul className="mt-2 space-y-1 text-sm text-green-700 ml-6 list-disc">
                <li>Vos courses sont automatiquement comptabilisées dans les challenges</li>
                <li>Aucune saisie manuelle nécessaire</li>
                <li>Badge "Vérifié Strava" sur vos activités</li>
            </ul>
            </div>
            
            {/* ✨ AJOUTER CE BOUTON */}
            <button
            onClick={() => window.location.href = '/strava-activities'}
            className="w-full px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition font-semibold flex items-center justify-center gap-2"
            >
            <Activity size={20} />
            Voir mes activités Strava
            </button>
        </div>
        )}

        {!stravaStatus.connected && (
          <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="text-sm text-orange-800">
              <p className="font-semibold mb-2">Pourquoi connecter Strava ?</p>
              <ul className="space-y-1 ml-4 list-disc text-orange-700">
                <li>Synchronisation automatique de vos courses</li>
                <li>Validation instantanée dans les challenges</li>
                <li>Aucune saisie manuelle requise</li>
              </ul>
            </div>
            
          </div>
        )}
      </div>
    </div>
  );
}

export default StravaConnect;